// Controle de acesso por perfil (Configurações > Controle de Acesso).
//
// O `accessControl` da empresa é um objeto { [tela]: { [perfil]: boolean } }.
// Antes essa configuração só escondia itens de menu — as rotas continuavam
// abertas, então bastava cair na URL (ou ser redirecionado pelo login) para ver
// a tela sem permissão. Este módulo concentra as regras para que menu e rota
// usem exatamente o mesmo critério.

// Telas oferecidas na tela de configuração (ordem = ordem da tabela).
export const SCREENS = [
  "Home",
  "Dashboard",
  "Clientes",
  "Pedidos",
  "Carrinhos",
  "Produtos",
  "Cupom Desc.",
  "Cashback",
  "Campanha",
  "Acervo",
  "Configurações",
  "Extrato",
  "Importações",
  "Mobile",
  "Vendedor(a)",
  "Tutorial",
  "MoodaChat",
  "Notificações",
  "Smart Look IA",
  "Provador Virtual",
  "Integrações",
  "ADS",
];

// Níveis abaixo da tela: Dashboard -> dash (Vendas, Clientes, Produtos) ->
// cards de cada dash. A chave guardada no `accessControl` é o caminho completo
// ("Dashboard/Vendas/Mapa de Vendas") — o rótulo sozinho colide entre níveis
// ("Vendas" é dash e card; "Clientes" é tela e dash). Só os cards (folhas) têm
// rota; o dash leva ao primeiro card permitido. Regra de herança em
// canAccessScreen.
const card = (dash, label, route) => ({
  key: `Dashboard/${dash}/${label}`,
  label,
  route,
});

export const SUB_SCREENS = {
  Dashboard: [
    {
      key: "Dashboard/Vendas",
      label: "Vendas",
      children: [
        card("Vendas", "Vendas", "indicadoresvendas"),
        card("Vendas", "Vend. Estratégica", "indicadoresestrategica"),
        card("Vendas", "Pagamentos", "indicadorespagamentos"),
        card("Vendas", "Mapa de Vendas", "indicadoresmapa"),
      ],
    },
    {
      key: "Dashboard/Clientes",
      label: "Clientes",
      children: [
        card("Clientes", "Cliente", "indicadoresclientes"),
        card("Clientes", "Pedidos Clientes", "relatorio-pedidos-clientes"),
      ],
    },
    {
      key: "Dashboard/Produtos",
      label: "Produtos",
      children: [
        card("Produtos", "Produto", "indicadoresprodutos"),
        card("Produtos", "Produto com Venda", "relatorio-produto-venda"),
        card("Produtos", "Avise-me quando chegar", "relatorio-avise"),
        card("Produtos", "Grade Incompleta", "relatorio-grade-incompleta"),
        card("Produtos", "Estoque", "relatorio-estoque"),
      ],
    },
  ],
};

// Nó -> chave do pai, e chave -> nó, para a árvore inteira.
const PARENT_SCREEN = {};
const SUB_NODE = {};
Object.entries(SUB_SCREENS).forEach(([screen, nodes]) =>
  (function index(parent, list) {
    list.forEach((node) => {
      PARENT_SCREEN[node.key] = parent;
      SUB_NODE[node.key] = node;
      if (node.children) index(node.key, node.children);
    });
  })(screen, nodes),
);

export const PROFILES = [
  "Administrador",
  "Gerente",
  "Vendedor(a)",
  "Estoquista",
  "Outros",
];

// Rota (relativa a /:companyId, em minúsculas) -> tela do controle de acesso.
// Rotas com parâmetro casam pelo prefixo: "products/123" -> "products".
const ROUTE_SCREENS = {
  home: "Home",
  homev2: "Home",

  customers: "Clientes",
  orders: "Pedidos",
  products: "Produtos",
  collection: "Acervo",
  discounts: "Cupom Desc.",
  cashback: "Cashback",
  campaign: "Campanha",
  chatbot: "MoodaChat",
  push: "Notificações",
  "virtual/showcase": "Smart Look IA",
  paymentreport: "Extrato",
  adsperformance: "ADS",
  integrationerror: "Integrações",

  carts: "Carrinhos",
  cartlist: "Carrinhos",

  tabdashboard: "Dashboard",
  dashcustomers: "Dashboard",
  indicadoresvendas: "Dashboard/Vendas/Vendas",
  indicadoresestrategica: "Dashboard/Vendas/Vend. Estratégica",
  indicadorespagamentos: "Dashboard/Vendas/Pagamentos",
  indicadoresmapa: "Dashboard/Vendas/Mapa de Vendas",
  indicadoresclientes: "Dashboard/Clientes/Cliente",
  leads: "Dashboard/Clientes/Cliente",
  "relatorio-pedidos-clientes": "Dashboard/Clientes/Pedidos Clientes",
  indicadoresprodutos: "Dashboard/Produtos/Produto",
  "relatorio-produtos": "Dashboard/Produtos/Produto",
  "relatorio-produto-venda": "Dashboard/Produtos/Produto com Venda",
  "relatorio-avise": "Dashboard/Produtos/Avise-me quando chegar",
  "relatorio-grade-incompleta": "Dashboard/Produtos/Grade Incompleta",
  "relatorio-estoque": "Dashboard/Produtos/Estoque",

  tabconfigs: "Configurações",
  company: "Configurações",
  users: "Configurações",
  usersrep: "Configurações",
  categories: "Configurações",
  colorsize: "Configurações",
  paleta: "Configurações",
  highlights: "Configurações",
  invoice: "Configurações",
  acontrol: "Configurações",

  tabimports: "Importações",
  importcustomers: "Importações",
  importproducts: "Importações",
  importstock: "Importações",
  importcategories: "Importações",
};

// Destino de cada tela, na ordem em que aparecem no menu lateral. Usado para
// escolher para onde mandar quem caiu numa tela sem permissão.
const SCREEN_ROUTES = [
  ["Home", "homev2"],
  ["Clientes", "customers"],
  ["Pedidos", "orders"],
  ["Carrinhos", "cartlist"],
  ["Produtos", "products"],
  ["Cupom Desc.", "discounts"],
  ["Cashback", "cashback"],
  ["Campanha", "campaign"],
  ["Acervo", "collection"],
  ["MoodaChat", "chatbot"],
  ["Notificações", "push"],
  ["Smart Look IA", "virtual/showcase"],
  ["Extrato", "paymentReport"],
  ["ADS", "indicadoresclientes?ads=1"],
  ["Dashboard", "tabdashboard"],
  ["Configurações", "tabconfigs"],
  ["Importações", "tabimports"],
  ["Integrações", "integrationerror"],
];

// Remove o slug da empresa e devolve a rota interna em minúsculas.
// "/moodabrasil/products/12" -> "products/12"
export function routeKeyFromPath(pathname) {
  const parts = String(pathname || "")
    .split("/")
    .filter(Boolean);
  return parts.slice(1).join("/").toLowerCase();
}

// Tela do controle de acesso correspondente à URL atual.
// Devolve undefined para rotas não mapeadas (que nunca são bloqueadas).
export function screenForPath(pathname, search = "") {
  let key = routeKeyFromPath(pathname);
  if (!key) return undefined;

  // "ADS" reaproveita a rota de indicadores de clientes com ?ads=1.
  if (key === "indicadoresclientes" && /(^|[?&])ads=1(&|$)/.test(String(search)))
    return "ADS";

  while (key) {
    if (ROUTE_SCREENS[key]) return ROUTE_SCREENS[key];
    const cut = key.lastIndexOf("/");
    if (cut < 0) return undefined;
    key = key.slice(0, cut);
  }
  return undefined;
}

// Regra única de permissão, usada pelo menu e pela guarda de rota.
//
// Sem configuração de acesso na empresa, ou sem perfil no usuário, nada é
// bloqueado — é o comportamento histórico das empresas que nunca preencheram a
// tabela. Tela não mapeada também passa.
//
// Dash/card: exige todos os níveis acima e só é negado quando desmarcado
// explicitamente (false). Sem valor gravado herda o pai — assim as empresas que
// configuraram o acesso antes desses níveis existirem continuam vendo tudo.
export function canAccessScreen(accessControl, position, screen) {
  if (!screen) return true;
  if (!accessControl || !position) return true;
  const parent = PARENT_SCREEN[screen];
  if (parent)
    return (
      canAccessScreen(accessControl, position, parent) &&
      accessControl[screen]?.[position] !== false
    );
  return accessControl[screen]?.[position] === true;
}

export function canAccessPath(accessControl, position, pathname, search = "") {
  return canAccessScreen(
    accessControl,
    position,
    screenForPath(pathname, search),
  );
}

// Rota do primeiro card permitido sob o nó (o próprio, se for card).
// null quando nada abaixo dele está liberado.
export function screenRoute(accessControl, position, key) {
  const node = SUB_NODE[key];
  if (!node || !canAccessScreen(accessControl, position, key)) return null;
  if (!node.children) return node.route;
  for (const child of node.children) {
    const route = screenRoute(accessControl, position, child.key);
    if (route) return route;
  }
  return null;
}

// Para onde mandar quem caiu numa tela sem permissão: sobe a árvore a partir
// do card negado e devolve o card permitido mais próximo (primeiro o mesmo
// dash, depois os outros dashes); sem nenhum, a primeira tela do menu.
export function fallbackRoute(accessControl, position, pathname, search = "") {
  let parent = PARENT_SCREEN[screenForPath(pathname, search)];
  while (parent) {
    const siblings = SUB_NODE[parent]?.children || SUB_SCREENS[parent] || [];
    for (const sib of siblings) {
      const route = screenRoute(accessControl, position, sib.key);
      if (route) return route;
    }
    parent = PARENT_SCREEN[parent];
  }
  return landingRoute(accessControl, position);
}

// Primeira rota permitida ao perfil, seguindo a ordem do menu.
// Devolve null quando o perfil não tem acesso a nenhuma tela.
export function landingRoute(accessControl, position) {
  if (!accessControl || !position) return "homev2";
  const found = SCREEN_ROUTES.find(([screen]) =>
    canAccessScreen(accessControl, position, screen),
  );
  return found ? found[1] : null;
}
